import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, Map as MapIcon, List as ListIcon, Gem, Car, Navigation, MapPin,
  Loader2, ArrowLeft, Star, AlertCircle, MessageCircle, User, Crown, X as XIcon,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import RideAndQRButtons from "../components/RideAndQRButtons";
import { useI18n } from "../lib/i18n";
import { haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import { sendVendorContact } from "../lib/sendRideRequest";
import * as guestStore from "../lib/guestStorage";
import FilterPlacesSheet, { DEFAULT_FILTERS, countActiveFilters, applyFilters } from "../components/FilterPlacesSheet";
import { CategoryLabel } from "../components/CategoryLabel";

/**
 * /saved — Dedicated "My Saved" page combining saved businesses + creator's
 * custom locations + saved vendors into one list with quick actions.
 */
export default function MySaved() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState(undefined);
  const [businesses, setBusinesses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customLocs, setCustomLocs] = useState([]);
  const [vipBiz, setVipBiz] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [filter, setFilter] = useState("all"); // all | businesses | vendors | gems | vip
  const { coords } = useGps();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  // iter133 — Shared FilterPlacesSheet state; applies to whichever tab
  // is currently active (business list when relevant).
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    getMe().then(async (me) => {
      setUser(me);
      if (!me) {
        // Guest: hydrate from localStorage
        try {
          const savedBiz = guestStore.getSaved().filter((s) => s.target_type === "business");
          const savedVen = guestStore.getSaved().filter((s) => s.target_type === "vendor");
          const customs = guestStore.getCustomLocations();
          const vipRows = guestStore.getVipReminders();
          setCustomLocs(customs);
          const [bizRows, venRows, vipDocs] = await Promise.all([
            Promise.all(savedBiz.map((s) => api.get(`/businesses/${s.target_id}`)
              .then((r) => ({ ...r.data, _savedAt: s.saved_at })).catch(() => null))),
            Promise.all(savedVen.map((s) => api.get(`/vendors/${s.target_id}`)
              .then((r) => ({ ...r.data, _savedAt: s.saved_at })).catch(() => null))),
            Promise.all(vipRows.map((r) => api.get(`/businesses/${r.business_id}`)
              .then((br) => ({ ...br.data, _addedAt: r.saved_at })).catch(() => null))),
          ]);
          setBusinesses(bizRows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
          setVendors(venRows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
          setVipBiz(vipDocs.filter(Boolean).sort((a, b) => (b._addedAt || "").localeCompare(a._addedAt || "")));
          const settingsResp = await api.get("/settings/public").then((r) => r.data || {}).catch(() => ({}));
          const flat = Array.isArray(settingsResp) ? Object.fromEntries(settingsResp.map((x) => [x.key, x.value])) : settingsResp;
          setSettings(flat);
        } catch { /* ignore */ }
        setLoading(false);
        return;
      }
      Promise.all([
        api.get("/favorites").then((r) => r.data || []),
        api.get("/custom-locations").then((r) => r.data || []),
        api.get("/settings/public").then((r) => r.data || {}).catch(() => ({})),
        api.get("/me/vip-reminders").then((r) => r.data || []).catch(() => []),
      ]).then(async ([favs, custom, s, vipRows]) => {
        setFavIds(favs);
        setCustomLocs(custom);
        const flat = Array.isArray(s) ? Object.fromEntries(s.map((x) => [x.key, x.value])) : s;
        setSettings(flat);

        // Resolve VIP reminders → full business docs
        const vipDocs = [];
        for (const r of vipRows) {
          if (!r.business_id) continue;
          try {
            const br = await api.get(`/businesses/${r.business_id}`);
            vipDocs.push({ ...br.data, _addedAt: r.created_at });
          } catch { /* removed */ }
        }
        vipDocs.sort((a, b) => (b._addedAt || "").localeCompare(a._addedAt || ""));
        setVipBiz(vipDocs);
        // Resolve business favorites
        const bizFavs = favs.filter((f) => f.target_type === "business");
        const rows = [];
        for (const f of bizFavs) {
          try {
            const r = await api.get(`/businesses/${f.target_id}`);
            rows.push({ ...r.data, _savedAt: f.created_at });
          } catch { /* deleted business */ }
        }
        rows.sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || ""));
        setBusinesses(rows);
        // Resolve vendor favorites
        const venFavs = favs.filter((f) => f.target_type === "vendor");
        const vrows = [];
        for (const f of venFavs) {
          try {
            const r = await api.get(`/vendors/${f.target_id}`);
            vrows.push({ ...r.data, _savedAt: f.created_at });
          } catch { /* deleted vendor */ }
        }
        vrows.sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || ""));
        setVendors(vrows);
        setLoading(false);
      }).catch(() => setLoading(false));
    });
  }, []);

  const unsaveBusiness = async (bid) => {
    if (!window.confirm("Remove from saved?")) return;
    try {
      if (user) await api.post("/favorites/toggle", { target_id: bid, target_type: "business" });
      else guestStore.toggleSaved(bid, "business");
      setBusinesses((p) => p.filter((b) => b.id !== bid));
      setFavIds((p) => p.filter((f) => !(f.target_id === bid && f.target_type === "business")));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const unsaveVendor = async (vid) => {
    if (!window.confirm("Remove from saved?")) return;
    try {
      if (user) await api.post("/favorites/toggle", { target_id: vid, target_type: "vendor" });
      else guestStore.toggleSaved(vid, "vendor");
      setVendors((p) => p.filter((v) => v.id !== vid));
      setFavIds((p) => p.filter((f) => !(f.target_id === vid && f.target_type === "vendor")));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  // iter133 — Apply the shared filters to the visible business list.
  // MUST be declared before any early-return to satisfy React hook rules.
  const visibleBusinesses = useMemo(
    () => applyFilters(businesses, filters, { coords }),
    [businesses, filters, coords],
  );
  const activeFilterCount = countActiveFilters(filters);

  if (user === undefined) {
    return <main className="min-h-screen" style={{ background: "#0B0B0B" }} />;
  }

  const showBiz = filter === "all" || filter === "businesses";
  const showVendors = filter === "all" || filter === "vendors";
  const showCustom = filter === "all" || filter === "gems";
  const showVip = filter === "vip";
  const isEmpty = !showVip && visibleBusinesses.length === 0 && vendors.length === 0 && customLocs.length === 0;
  const vipEmpty = showVip && vipBiz.length === 0;
  const rideWa = settings.whatsapp_general_ride || settings.transportation_whatsapp;

  const removeVip = async (bizId) => {
    const prev = vipBiz;
    const removed = prev.find((b) => b.id === bizId);
    setVipBiz(prev.filter((b) => b.id !== bizId));
    try {
      if (user) {
        await api.delete(`/businesses/${bizId}/vip-reminder`);
      } else {
        guestStore.toggleVipReminder(bizId); // removes since it currently exists
      }
      toast(t("saved_vip_removed_undo") || "Removed", {
        action: {
          label: t("undo") || "Undo",
          onClick: async () => {
            try {
              if (user) await api.post(`/businesses/${bizId}/vip-reminder`);
              else guestStore.toggleVipReminder(bizId); // re-add
              if (removed) setVipBiz((p) => [removed, ...p]);
            } catch { /* ignore */ }
          },
        },
      });
    } catch {
      setVipBiz(prev);
      toast.error("Could not remove");
    }
  };

  return (
    <main data-testid="my-saved-page" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-4 pb-3 flex items-center justify-between sticky top-0 z-10" style={{ background: "rgba(11,11,11,0.92)", backdropFilter: "blur(8px)" }}>
        <button onClick={() => navigate(-1)} aria-label="Back" className="p-2 -ml-2">
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 className="font-display text-xl text-foreground">My Saved</h1>
        <div className="flex items-center gap-2">
          {/* iter133 — Shared filter sheet button */}
          <button
            type="button"
            data-testid="my-saved-filter-btn"
            aria-label="Open filters"
            onClick={() => setFilterOpen(true)}
            className="relative grid place-items-center rounded-full hover:bg-[rgba(232,184,75,0.08)] transition-colors"
            style={{
              width: 36,
              height: 36,
              border: "1px solid rgba(232,184,75,0.5)",
              color: "#E8B84B",
            }}
          >
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
            {activeFilterCount > 0 && (
              <span
                data-testid="my-saved-filter-badge"
                className="absolute"
                style={{
                  top: -6, right: -6,
                  minWidth: 18, height: 18, padding: "0 5px",
                  background: "#E8B84B", color: "#1a0f06",
                  borderRadius: 9999,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11, fontWeight: 700,
                  display: "grid", placeItems: "center",
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/map?saved=1")}
            data-testid="my-saved-map-view-btn"
            aria-label="Open map view"
            className="p-2 -mr-2 text-gold"
          >
            <MapIcon className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Filter chips */}
      <div className="px-5 mt-2 flex gap-2 overflow-x-auto" data-testid="my-saved-filter-chips" style={{ scrollbarWidth: "none" }}>
        {[
          { key: "all", label: t("saved_tab_all") || "All" },
          { key: "businesses", label: t("saved_tab_businesses") || "Businesses" },
          { key: "vendors", label: t("saved_tab_vendors") || "Vendors" },
          { key: "gems", label: t("saved_tab_gems") || "Gems" },
          { key: "vip", label: t("saved_tab_vip") || "VIP" },
        ].map((c) => (
          <button
            key={c.key}
            type="button"
            data-testid={`my-saved-chip-${c.key}`}
            onClick={() => setFilter(c.key)}
            style={{
              padding: "8px 14px",
              minHeight: 40,
              fontSize: 12,
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: 1,
              textTransform: "uppercase",
              borderRadius: 999,
              background: filter === c.key ? "#E8B84B" : "transparent",
              color: filter === c.key ? "#0B0B0B" : "#E8B84B",
              border: filter === c.key ? "1px solid #E8B84B" : "1px solid rgba(232,184,75,0.4)",
              whiteSpace: "nowrap",
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="px-5 mt-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" /></div>
      ) : showVip ? (
        vipEmpty ? (
          <EmptyVip />
        ) : (
          <div className="px-5 mt-6">
            <ul className="space-y-2.5" data-testid="my-saved-vip-section">
              {vipBiz.map((b) => (
                <li key={b.id} className="lux-card p-3 flex items-center gap-3" data-testid={`my-saved-vip-${b.id}`}>
                  <Thumb url={b.photos?.[0]?.url} icon={Crown} />
                  <div className="flex-1 min-w-0">
                    <Link to={`/places/${b.id}`} className="font-display text-base text-foreground truncate block">{b.name}</Link>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] uppercase tracking-widest text-gold px-1.5 py-0.5" style={{ border: "1px solid rgba(232,184,75,0.4)" }}>
                        <Crown className="w-2.5 h-2.5 inline-block mr-1" strokeWidth={1.5} /> VIP
                      </span>
                      <span className="text-[11px] text-foreground/45"><CategoryLabel category={b.category} subcategory={b.subcategory} /></span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeVip(b.id)}
                    data-testid={`my-saved-vip-remove-${b.id}`}
                    aria-label="Remove from VIP"
                    className="grid place-items-center text-foreground/55 hover:text-gold"
                    style={{ width: 36, height: 36, borderRadius: 999, border: "1px solid rgba(232,184,75,0.3)" }}
                  >
                    <XIcon className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      ) : isEmpty ? (
        <EmptyAll />
      ) : (
        <div className="px-5 mt-6 space-y-8">
          {showBiz && (
            <section data-testid="my-saved-businesses-section">
              <h2 className="eyebrow text-gold mb-3">Saved Businesses</h2>
              {visibleBusinesses.length === 0 ? (
                <EmptyBusinesses />
              ) : (
                <ul className="space-y-2.5">
                  {visibleBusinesses.map((b) => (
                    <li key={b.id} className="lux-card p-3" data-testid={`my-saved-biz-${b.id}`}>
                      <div className="flex items-center gap-3">
                        <Thumb url={b.photos?.[0]?.url} icon={Star} />
                        <div className="flex-1 min-w-0">
                          <Link to={`/places/${b.id}`} className="font-display text-base text-foreground truncate block">{b.name}</Link>
                          <div className="text-[11px] text-foreground/55 mt-0.5">
                            <CategoryLabel category={b.category} subcategory={b.subcategory} />
                            {distanceLabel(coords, b)}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label="Unsave"
                          data-testid={`unsave-${b.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); unsaveBusiness(b.id); }}
                          className="w-9 h-9 grid place-items-center rounded-full text-gold shrink-0"
                          style={{ border: "1px solid rgba(232,184,75,0.4)" }}
                        >
                          <Heart className="w-3.5 h-3.5" strokeWidth={2} fill="#E8B84B" />
                        </button>
                      </div>
                      {b.lat != null && b.lng != null && (
                        <div className="mt-3">
                          <RideAndQRButtons
                            destination={{
                              id: b.id,
                              name: b.name,
                              address: b.address,
                              lat: b.lat,
                              lng: b.lng,
                              category: b.category,
                              neighborhood: b.neighborhood,
                              phone: b.phone,
                              whatsapp: b.whatsapp,
                            }}
                            testidPrefix={`saved-biz-${b.id}`}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {showVendors && (
            <section data-testid="my-saved-vendors-section">
              <h2 className="eyebrow text-gold mb-3">My Vendors</h2>
              {vendors.length === 0 ? (
                <EmptyVendors />
              ) : (
                <ul className="space-y-2.5">
                  {vendors.map((v) => (
                    <li key={v.id} className="lux-card p-3 flex items-center gap-3" data-testid={`my-saved-vendor-${v.id}`}>
                      <VendorThumb vendor={v} />
                      <div className="flex-1 min-w-0">
                        <Link to={`/vendors/${v.id}`} className="font-display text-base text-foreground truncate block">{v.full_name}</Link>
                        <div className="text-[11px] text-foreground/55 mt-0.5">
                          {prettyCategory(v.category) || "Vendor"}
                          {distanceLabel(coords, v)}
                        </div>
                      </div>
                      <VendorQuickActions target={v} onUnsave={() => unsaveVendor(v.id)} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {showCustom && (
            <section data-testid="my-saved-custom-section">
              <h2 className="eyebrow text-gold mb-3">My Local Gems</h2>
              {customLocs.length === 0 ? (
                <EmptyCustom />
              ) : (
                <ul className="space-y-2.5">
                  {customLocs.map((c) => (
                    <li key={c.id} className="lux-card p-3" data-testid={`my-saved-custom-${c.id}`}>
                      <div className="flex items-center gap-3">
                        <Thumb url={c.photos?.[0]?.url} icon={Gem} />
                        <div className="flex-1 min-w-0">
                          <Link to={`/my-places/custom/${c.id}`} className="font-display text-base text-foreground truncate block">{c.name}</Link>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-[10px] uppercase tracking-widest text-gold px-1.5 py-0.5" style={{ border: "1px solid rgba(232,184,75,0.4)" }}>
                              <Gem className="w-2.5 h-2.5 inline-block mr-1" strokeWidth={1.5} /> Local Gem
                            </span>
                            {c.directory_request_status === "pending" && (
                              <span className="text-[10px] uppercase tracking-widest text-amber-300 px-1.5 py-0.5" style={{ border: "1px solid rgba(245,158,11,0.4)" }}>Pending</span>
                            )}
                            {distanceLabel(coords, c) && <span className="text-[11px] text-foreground/45">{distanceLabel(coords, c)}</span>}
                          </div>
                        </div>
                      </div>
                      {c.lat != null && c.lng != null && (
                        <div className="mt-3">
                          <RideAndQRButtons
                            destination={{
                              id: c.id,
                              name: c.name,
                              address: c.address,
                              lat: c.lat,
                              lng: c.lng,
                              category: c.category,
                              neighborhood: c.neighborhood,
                              phone: c.phone,
                              whatsapp: c.whatsapp,
                            }}
                            testidPrefix={`saved-gem-${c.id}`}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                  <li className="pt-2">
                    <Link to="/my-places/custom/new" className="block text-center font-display text-sm text-gold py-3" style={{ border: "1px dashed rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}>
                      + Add a Location
                    </Link>
                  </li>
                </ul>
              )}
            </section>
          )}
        </div>
      )}

      <FilterPlacesSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        currentFilters={filters}
        businesses={businesses}
        resultCount={visibleBusinesses.length}
      />

      <BottomNav />
    </main>
  );
}

function Thumb({ url, icon: Icon }) {
  return (
    <div className="w-[60px] h-[60px] rounded-lg overflow-hidden grid place-items-center flex-shrink-0" style={{ background: "#171717" }}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Icon className="w-7 h-7 text-gold opacity-70" strokeWidth={1.5} />}
    </div>
  );
}

function VendorThumb({ vendor }) {
  const photo = vendor.photos?.[0];
  const photoSrc = photo?.thumbnail || photo?.medium || photo?.url
    || vendor.selfie_photo?.thumbnail || vendor.selfie_photo?.medium;
  const url = photoSrc ? (photoSrc.startsWith?.("http") ? photoSrc : `${process.env.REACT_APP_BACKEND_URL}${photoSrc}`) : null;
  return <Thumb url={url} icon={User} />;
}

function VendorQuickActions({ target, onUnsave }) {
  const hasGeo = target.location_visible && target.lat != null && target.lng != null;
  const wa = (target.whatsapp || "").replace(/\D/g, "");
  return (
    <div className="flex items-center gap-1.5">
      {wa && (
        <button
          type="button"
          aria-label="WhatsApp"
          data-testid={`vendor-wa-${target.id}`}
          onClick={(e) => { e.stopPropagation(); sendVendorContact({ name: target.full_name || target.name, whatsapp: target.whatsapp, vendor_id: target.id }, { source: "my-saved-vendor" }); }}
          className="w-9 h-9 grid place-items-center rounded-full text-white"
          style={{ background: "#25D366", border: "none" }}
        >
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
      {hasGeo && (
        <a
          aria-label="Navigate"
          data-testid={`vendor-nav-${target.id}`}
          href={`https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}`}
          target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="w-9 h-9 grid place-items-center rounded-full text-gold"
          style={{ border: "1px solid rgba(232,184,75,0.4)" }}
        >
          <Navigation className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>
      )}
      <button
        type="button"
        aria-label="Unsave"
        data-testid={`vendor-unsave-${target.id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnsave?.(); }}
        className="w-9 h-9 grid place-items-center rounded-full text-gold"
        style={{ border: "1px solid rgba(232,184,75,0.4)" }}
      >
        <Heart className="w-3.5 h-3.5" strokeWidth={2} fill="#E8B84B" />
      </button>
    </div>
  );
}

function EmptyAll() {
  return (
    <div className="px-8 mt-16 text-center" data-testid="my-saved-empty-all">
      <Heart className="w-9 h-9 text-gold mx-auto mb-4" strokeWidth={1.5} />
      <p className="text-foreground/70 text-[15px]">You have not saved anything yet. Explore places to get started.</p>
      <Link to="/places" className="mt-6 inline-block px-7 py-3 font-display text-base" style={{ background: "#E8B84B", color: "#0B0B0B", borderRadius: 10, minHeight: 48 }}>
        Explore Places
      </Link>
    </div>
  );
}
function EmptyBusinesses() {
  return (
    <div className="px-3 py-6 text-center text-[13px] text-foreground/55" data-testid="my-saved-empty-biz">
      You have not saved any businesses yet. Tap the heart on any business to save it.
    </div>
  );
}
function EmptyVendors() {
  return (
    <div className="px-3 py-6 text-center text-[13px] text-foreground/55" data-testid="my-saved-empty-vendors">
      You have not saved any vendors yet. Discover vendors through QR codes or shared links.
    </div>
  );
}
function EmptyCustom() {
  return (
    <div className="px-3 py-4 text-center" data-testid="my-saved-empty-custom">
      <p className="text-[13px] text-foreground/55 mb-3">You have not created any custom locations yet.</p>
      <Link to="/my-places/custom/new" className="inline-block px-6 py-2.5 font-display text-sm" style={{ background: "#E8B84B", color: "#0B0B0B", borderRadius: 10, minHeight: 44 }}>
        Add a Location
      </Link>
    </div>
  );
}
function EmptyVip() {
  return (
    <div className="px-6 py-10 text-center" data-testid="my-saved-empty-vip">
      <Crown className="w-8 h-8 mx-auto text-gold/60 mb-3" strokeWidth={1.5} />
      <p className="text-[14px] text-foreground/65 leading-snug">
        Toca <span className="text-gold">Solicitar VIP</span> en cualquier negocio para añadirlo aquí.
      </p>
    </div>
  );
}

function prettyCategory(c) { return (c || "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()); }
function distanceLabel(coords, t) {
  if (!coords || t.lat == null || t.lng == null) return null;
  const km = haversineKm(coords, { lat: t.lat, lng: t.lng });
  const s = fmtDistanceWalk(km);
  return s ? ` · ${s}` : null;
}

